import React, { useRef, useState, useEffect } from 'react';
import PropTypes from "prop-types";
import Select from "@mui/material/Select";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import MenuItem from "@mui/material/MenuItem";
import { FormControl, InputLabel, FormHelperText, Tooltip } from "@mui/material";

const useStyles = makeStyles(() => ({
  formControl: {
    fontSize: 12,
    padding: "9px 12px",
    height: "100%",
    color: "#66615b",
    width: "100%",
    display: "flex",
    alignItems: "center",
    "&.Mui-disabled": {
      opacity: 0.3,
    },
  },
  label: {
    fontSize: 10,
    color: "#9A9A9A",
    fontWeight: 600,
    textTransform: "uppercase",
  },
  menuItem: {
    fontSize: 12,
  },
  formHelperText: {
    display: "inline-block",
    marginTop: -1,
    fontSize: 10,
  },
  inputLabelText: {
    fontSize: 12,
    "&:not(.MuiInputLabel-shrink)": {
      transform: "translate(14px, 9px) scale(1)",
    },
  },
}));

const CustomSelect = ({
  onChange,
  value,
  options,
  disabled,
  native,
  inputLabel,
  name,
  helperText,
  error,
  customClass,
  multiple,
  renderValue,
  open,
  onOpen,
  shrink,
  size,
  menuItemClass,
  formControlClass,
  id,
  inputProps: inputPropsProp,
  defaultOption,
  onClose,
  required,
  fullWidth,
}) => {
  const classes = useStyles();

  const textRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const getSelectedLabel = (selected) => {
    if (Array.isArray(options)) {
      const found = options.find(opt => opt.value === selected);
      return found ? found.label : "";
    }
    return "";
  };

  const selectedLabel = getSelectedLabel(value);

  const defaultInputSx = {
    padding: "unset",
    paddingRight: "16px !important",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  useEffect(() => {
    if (textRef.current) {
      const isOverflowing = textRef.current.scrollWidth > textRef.current.clientWidth;
      setShowTooltip(isOverflowing);
    }
  }, [selectedLabel]);

  return (
    <FormControl
      required={required}
      className={customClass}
      variant="outlined"
      size={size}
      fullWidth={fullWidth}
    >
      <InputLabel shrink={shrink} size={size} className={classes.inputLabelText}>
        {inputLabel}
      </InputLabel>
      <Select
        id={id}
        open={open}
        onOpen={onOpen}
        error={error}
        onChange={onChange}
        value={value === "" ? 0 : value}
        disabled={disabled}
        name={name}
        fullWidth={fullWidth}
        renderValue={renderValue || ((selected) => (
          <Tooltip arrow title={selectedLabel} placement="top" disableHoverListener={!showTooltip}>
            <span
              ref={textRef}
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
              }}
            >
              {selectedLabel}
            </span>
          </Tooltip>
        ))}
        multiple={multiple}
        variant="outlined"
        label={inputLabel}
        onClose={onClose}
        className={clsx(classes.formControl, formControlClass)}

        inputProps={{
          ...inputPropsProp,
          sx: {
            ...defaultInputSx,
            ...(inputPropsProp?.sx || {}),
          }
        }}
      >
        {defaultOption && <MenuItem value="0">{defaultOption}</MenuItem>}
        {options.map((x, index) => (
          <MenuItem
            className={menuItemClass || classes.menuItem}
            value={x.value}
            disabled={x.disabled}
            // eslint-disable-next-line react/no-array-index-key
            key={`${x.value}-${index}`}
          >
            {x.label}
          </MenuItem>
        ))}
      </Select>
      {helperText && (
        <FormHelperText className={classes.formHelperText} error={error}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

CustomSelect.defaultProps = {
  disabled: false,
  native: false,
  name: "",
  value: "",
  helperText: null,
  error: false,
  multiple: false,
  inputLabel: null,
  inputProps: {},
  customClass: "",
  renderValue: null,
  size: "normal",
  defaultOption: null,
  fullWidth: true,
};

CustomSelect.propTypes = {
  multiple: PropTypes.bool,
  helperText: PropTypes.string,
  error: PropTypes.bool,
  name: PropTypes.string,
  disabled: PropTypes.bool,
  native: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        .isRequired,
      label: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        .isRequired,
    })
  ).isRequired,
  inputLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  customClass: PropTypes.string,
  renderValue: PropTypes.node,
  defaultOption: PropTypes.node,
  inputProps: PropTypes.object,
};

export default CustomSelect;
